import { useSearchParams } from 'next/navigation';
import { InfiniteData, useInfiniteQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseInfiniteQueryCampaignsAvailableOffersData =
  TravelPortal.Responses.CampaignsAvailableOffers;

interface UseInfiniteQueryCampaignsAvailableOffersOptions {
  initialData?: InfiniteData<UseInfiniteQueryCampaignsAvailableOffersData>;
}

interface UseQueryCampaignsAvailableOffersParameters {
  campaignId: number | null;
  campaignType: TravelPortal.Properties.CampaignType;
}

export const useInfiniteQueryCampaignsAvailableOffers = (
  parameters: UseQueryCampaignsAvailableOffersParameters,
  options?: UseInfiniteQueryCampaignsAvailableOffersOptions,
) => {
  const searchParams = useSearchParams();
  const locale = getLocale();

  const query = useInfiniteQuery<UseInfiniteQueryCampaignsAvailableOffersData>(
    [
      QueryId.CAMPAIGNS_AVAILABLE_OFFERS,
      parameters,
      locale,
      searchParams!.toString(),
    ],
    async ({ pageParam = 0 }) => {
      const baseUrl = '/api/campaigns/available-offers';

      const searchParameters = new URLSearchParams({
        area: String(searchParams!.get('area')),
        campaignId: String(parameters.campaignId),
        campaignType: String(parameters.campaignType),
        skip: pageParam.toString(),
      });

      if (searchParams!.get('adultsNumber')) {
        searchParameters.append(
          'adultsNumber',
          String(searchParams!.get('adultsNumber')),
        );
      }

      if (searchParams!.get('batchSize')) {
        searchParameters.append(
          'batchSize',
          String(searchParams!.get('batchSize')),
        );
      }

      if (searchParams!.get('childrenAges')) {
        searchParameters.append(
          'childrenAges',
          String(searchParams!.get('childrenAges')),
        );
      }

      if (searchParams!.get('departureDate')) {
        searchParameters.append(
          'departureDate',
          String(searchParams!.get('departureDate')),
        );
      }

      if (searchParams!.get('departureIds')) {
        searchParameters.append(
          'departureIds',
          String(searchParams!.get('departureIds')),
        );
      }

      if (searchParams!.get('countryIds')) {
        searchParameters.append(
          'countryIds',
          String(searchParams!.get('countryIds')),
        );
      }

      if (searchParams!.get('destinationIds')) {
        searchParameters.append(
          'destinationIds',
          String(searchParams!.get('destinationIds')),
        );
      }

      if (searchParams!.get('durationRange')) {
        searchParameters.append(
          'durationRange',
          String(searchParams!.get('durationRange')),
        );
      }

      if (searchParams!.get('durationType')) {
        searchParameters.append(
          'durationType',
          String(searchParams!.get('durationType')),
        );
      }

      if (searchParams!.get('filterFacilityIds')) {
        searchParameters.append(
          'filterFacilityIds',
          String(searchParams!.get('filterFacilityIds')),
        );
      }

      if (searchParams!.get('filterFacilityValues')) {
        searchParameters.append(
          'filterFacilityValues',
          String(searchParams!.get('filterFacilityValues')),
        );
      }

      if (searchParams!.get('filterPricePerPerson')) {
        searchParameters.append(
          'filterPricePerPerson',
          String(searchParams!.get('filterPricePerPerson')),
        );
      }

      if (searchParams!.get('filterProviders')) {
        searchParameters.append(
          'filterProviders',
          String(searchParams!.get('filterProviders')),
        );
      }

      if (searchParams!.get('filterStars')) {
        searchParameters.append(
          'filterStars',
          String(searchParams!.get('filterStars')),
        );
      }

      if (searchParams!.get('maxDepartureDate')) {
        searchParameters.append(
          'maxDepartureDate',
          String(searchParams!.get('maxDepartureDate')),
        );
      }

      if (searchParams!.get('sortBy')) {
        searchParameters.append('sortBy', String(searchParams!.get('sortBy')));
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error(
            'hooks.useInfiniteQueryCampaignsAvailableOffers',
            error,
          );

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!searchParams!.get('area') && !!parameters.campaignType,
      getNextPageParam: lastPage => {
        const { pagination } = lastPage;
        const totalResults = pagination.count;
        const currentSkip = pagination.skip;
        const currentTake = pagination.take;

        const nextSkip = currentSkip + currentTake;

        if (nextSkip < totalResults) {
          return nextSkip;
        }

        return undefined;
      },
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};
